import { AxiosError, AxiosRequestConfig } from 'axios';
import { useEffect, useState } from 'react';

import { get, publicGet, post } from '.';

const axiosFunctions = { get, publicGet, post };

export type UseFetchReturn<T> = {
    data: T | null;
    loading: boolean;
    error: AxiosError | undefined;
    status: number;
    abortController: AbortController;
};

const useFetch = <T>(
    functionName: keyof typeof axiosFunctions,
    url: string,
    axiosOptions?: AxiosRequestConfig
) => {
    const [responseData, setResponseData] = useState<T | null>(null);
    const [error, setError] = useState<AxiosError>();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<number>(0);
    const [abortController] = useState<AbortController>(new AbortController());
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
        axiosFunctions[functionName](url, { signal: abortController.signal, ...axiosOptions })
            .then((axiosRes) => {
                !axiosRes.data
                    ? setError(new AxiosError('Received null or undefined as a response'))
                    : setResponseData(axiosRes.data);
                setStatus(axiosRes.status);
            })
            .catch((error) => {
                const axErr = error as AxiosError;
                setError(axErr);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [abortController, url, axiosOptions, functionName, refresh]);

    function refreshData() {
        setRefresh((prev) => !prev);
    }

    return {
        data: responseData,
        loading,
        error,
        status,
        abortController,
        refreshData
    };
};

const useGet = <T>(url: string, axiosOptions?: AxiosRequestConfig) =>
    useFetch<T>('get', url, axiosOptions);

const useGetPublic = <T>(url: string, axiosOptions?: AxiosRequestConfig) =>
    useFetch<T>('publicGet', url, axiosOptions);

const usePost = <D, T>(url: string, payload: D, axiosOptions?: AxiosRequestConfig) => {
    const [responseData, setResponseData] = useState<T | null>(null);
    const [error, setError] = useState<AxiosError>();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<number>(0);
    const [abortController] = useState<AbortController>(new AbortController());
    const [refresh, setRefresh] = useState(false);

    useEffect(() => {
        post(url, payload, { signal: abortController.signal, ...axiosOptions })
            .then((axiosRes) => {
                !axiosRes.data
                    ? setError(new AxiosError('Received null or undefined as a response'))
                    : setResponseData(axiosRes.data);
                setStatus(axiosRes.status);
            })
            .catch((error) => {
                const axErr = error as AxiosError;
                setError(axErr);
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [abortController, url, axiosOptions, refresh]);

    function refreshData() {
        setRefresh((prev) => !prev);
    }

    return {
        data: responseData,
        loading,
        error,
        status,
        abortController,
        refreshData
    };
};

export { useGet, useGetPublic, usePost };
